import { Globe, User, Users } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router'

import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { useEmailTemplates } from '@/models/emailTemplate'
import { OthersTab } from './OthersTab'
import { TemplatesPane } from './TemplatesPane'

enum TemplatesTab {
  GLOBAL = 'global',
  PERSONAL = 'personal',
  OTHERS = 'others',
}

function tabFromHash(hash: string): TemplatesTab {
  const value = hash.slice(1)
  return Object.values<string>(TemplatesTab).includes(value) ? (value as TemplatesTab) : TemplatesTab.GLOBAL
}

export function EmailTemplatesIndex() {
  const location = useLocation()
  const navigate = useNavigate()
  const tab = tabFromHash(location.hash)
  const { data: templates = [], isLoading } = useEmailTemplates()

  function onTabChange(value: string) {
    void navigate(
      { search: location.search, hash: value === TemplatesTab.GLOBAL ? '' : value },
      { replace: true, preventScrollReset: true }
    )
  }

  return (
    <>
      <Helmet>
        <title>Email Templates</title>
      </Helmet>
      <div className="flex flex-col gap-6 p-6">
        <h1 className="text-2xl font-semibold">Email Templates</h1>
        <ToggleTabs
          value={tab}
          onValueChange={onTabChange}
          className="w-full gap-4"
          items={[
            { value: TemplatesTab.GLOBAL, icon: <Globe />, label: 'Global' },
            { value: TemplatesTab.PERSONAL, icon: <User />, label: 'Personal' },
            { value: TemplatesTab.OTHERS, icon: <Users />, label: 'Others' },
          ]}
        >
          {tab === TemplatesTab.GLOBAL && (
            <TemplatesPane
              templates={templates.filter((t) => t.isGlobal)}
              isLoading={isLoading}
              canManage
              create={{ isGlobal: true }}
              emptyText="No global templates yet."
            />
          )}
          {tab === TemplatesTab.PERSONAL && (
            <TemplatesPane
              templates={templates.filter((t) => !t.isGlobal)}
              isLoading={isLoading}
              canManage
              create={{ isGlobal: false }}
              emptyText="No personal templates yet."
            />
          )}
          {tab === TemplatesTab.OTHERS && <OthersTab />}
        </ToggleTabs>
      </div>
    </>
  )
}
